import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';
import { queryDataSource } from '../../api/query';
import { sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';
import { GRUP_RENK } from './kanalRenk';
import { subeSuz } from './subeSuzgeci';

interface KanalSatiri {
  tarih: string;
  // 040 fix4'ten beri lokasyon TASIYOR; sube suzgeci bunu kullanir.
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: number;
  net_ciro: number;
}

interface KanalOzet {
  kanal: string;
  ciro: number;
  fis: number;
  sepet: number | null;
}

interface GrupOzet {
  grup: string;
  ciro: number;
  fis: number;
  pay: number;
  sepet: number | null;
  kanallar: KanalOzet[];
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
  /** null = tum subeler. */
  sube?: string | null;
}


/** Pay yuzdesi: Delta/fmtPct'nin '+' onekli delta bicimi burada UYGUN DEGIL
 * (bu bir degisim degil, bir pay) - o yuzden ayri, isaretsiz bicimlendirici. */
function fmtYuzde(n: number): string {
  return `${n.toFixed(1).replace('.', ',')}%`;
}

export function KanalBlok({ reports, donem, sube = null }: Props) {
  const [acikGrup, setAcikGrup] = useState<string | null>(null);
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  // CONTROLLER RULING F4: sonek 40 + ad kontrolu. Sonek kimligi firmalar
  // arasi bir GARANTI degil GOZLEMDIR (1-34 tutuyor, 35'te kirildi -
  // 995035 baska firmada "Acik Hesaplar"). Ad tutmazsa blok sessizce
  // gizlenir, yanlis firmanin verisini basmaz.
  const id = sourceIdBul(reports, 40, 'Satış Kanalı Özeti');

  const kanalQ = useQuery({
    queryKey: ['sa-kanal', id, params],
    queryFn: () => queryDataSource<KanalSatiri>({ sourceId: id!, parameters: params }),
    enabled: !!id,
    throwOnError: false,
  });

  const gruplar = useMemo<GrupOzet[]>(() => {
    const rows = subeSuz(kanalQ.data?.data ?? [], sube);
    const toplamCiro = rows.reduce((t, r) => t + Number(r.net_ciro ?? 0), 0);

    const grupMap = new Map<
      string,
      { ciro: number; fis: number; kanalMap: Map<string, { ciro: number; fis: number }> }
    >();
    for (const r of rows) {
      const grupAd = String(r.kanal_grup);
      const kanalAd = String(r.kanal);
      if (!grupMap.has(grupAd)) {
        grupMap.set(grupAd, { ciro: 0, fis: 0, kanalMap: new Map() });
      }
      const g = grupMap.get(grupAd)!;
      g.ciro += Number(r.net_ciro ?? 0);
      g.fis += Number(r.fis_sayisi ?? 0);

      if (!g.kanalMap.has(kanalAd)) g.kanalMap.set(kanalAd, { ciro: 0, fis: 0 });
      const k = g.kanalMap.get(kanalAd)!;
      k.ciro += Number(r.net_ciro ?? 0);
      k.fis += Number(r.fis_sayisi ?? 0);
    }

    return Array.from(grupMap.entries())
      .map(([grup, v]) => ({
        grup,
        ciro: v.ciro,
        fis: v.fis,
        pay: toplamCiro > 0 ? (v.ciro / toplamCiro) * 100 : 0,
        sepet: v.fis > 0 ? v.ciro / v.fis : null,
        kanallar: Array.from(v.kanalMap.entries())
          .map(([kanal, kv]) => ({
            kanal,
            ciro: kv.ciro,
            fis: kv.fis,
            sepet: kv.fis > 0 ? kv.ciro / kv.fis : null,
          }))
          .sort((a, b) => b.ciro - a.ciro),
      }))
      .sort((a, b) => b.ciro - a.ciro);
  }, [kanalQ.data, sube]);

  if (!id) return null; // rapor yok/ad tutmuyor -> sorgu bile acilmaz (enabled=false)
  if (gruplar.length < 2) return null; // bos durum kurali: tek kanal grubu -> blok gizlenir

  return (
    <div data-testid="blok-kanal">
      <SectionCard
        title="Satış kanalı"
        subtitle="Mağaza / pazaryeri / kurumsal / online kırılımı"
      >
        <div className="flex flex-col gap-4">
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={gruplar}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => fmtNum(Number(v))}
                  tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                  axisLine={{ stroke: demoTheme.border }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="grup"
                  tick={{ fontSize: 12, fill: demoTheme.textPrimary }}
                  axisLine={false}
                  tickLine={false}
                  width={88}
                />
                <ChartTooltip formatter={(v) => fmtTRY(Number(v))} />
                <Bar dataKey="ciro" radius={[0, 2, 2, 0]}>
                  {gruplar.map((g) => (
                    <Cell key={g.grup} fill={GRUP_RENK[g.grup] ?? demoTheme.textMuted} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-col">
            {gruplar.map((g) => {
              const acik = acikGrup === g.grup;
              return (
                <div key={g.grup} data-testid={`kanal-${g.grup}`} className="border-b border-border last:border-b-0">
                  <button
                    type="button"
                    data-testid={`kanal-ac-${g.grup}`}
                    onClick={() => setAcikGrup((cur) => (cur === g.grup ? null : g.grup))}
                    className="focus-ring w-full flex items-center justify-between gap-3 py-3 text-left"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: GRUP_RENK[g.grup] ?? demoTheme.textMuted }}
                      />
                      <span className="text-[13px] font-medium text-ink-900 truncate">{g.grup}</span>
                    </span>
                    <span className="flex items-center gap-4 shrink-0 tnum">
                      <span className="text-[13px] text-ink-900">{fmtTRY(g.ciro)}</span>
                      <span className="text-[12px] text-ink-500 w-12 text-right">{fmtYuzde(g.pay)}</span>
                      <span className="text-[12px] text-ink-500 w-20 text-right">
                        {g.sepet !== null ? fmtTRY(g.sepet) : '—'}
                      </span>
                    </span>
                  </button>
                  {acik && (
                    <div className="pl-4 pb-3 flex flex-col gap-1.5">
                      {g.kanallar.map((k) => (
                        <div
                          key={k.kanal}
                          className="flex items-center justify-between gap-3 text-[12px] text-ink-600"
                        >
                          <span className="truncate">{k.kanal}</span>
                          <span className="flex items-center gap-4 shrink-0 tnum">
                            <span>{fmtTRY(k.ciro)}</span>
                            <span className="w-20 text-right">
                              {k.sepet !== null ? fmtTRY(k.sepet) : '—'}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
